import { Component, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';
import { GlobalService } from 'src/app/_services/global.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-mascot',
  templateUrl: './mascot.component.html',
  styleUrls: ['./mascot.component.scss'],
  animations: [
    trigger('bounce', [
      state('idle', style({ transform: 'translateY(0)' })),
      state('up', style({ transform: 'translateY(-8px)' })),
      transition('idle <=> up', animate('1.5s ease-in-out'))
    ]),
    trigger('clickReact', [
      transition(':enter', []),
      transition('* => clicked', [
        animate('0.4s', keyframes([
          style({ transform: 'scale(1)', offset: 0 }),
          style({ transform: 'scale(1.2) rotate(10deg)', offset: 0.3 }),
          style({ transform: 'scale(0.9) rotate(-5deg)', offset: 0.6 }),
          style({ transform: 'scale(1)', offset: 1.0 })
        ]))
      ])
    ])
  ]
})
export class MascotComponent implements OnInit, OnDestroy {
  @Output() mascotClicked = new EventEmitter<void>();

  bounceState = 'idle';
  clickState = 'idle';
  isTransitioning = false;
  currLang: any = 'en';

  particles = Array.from({ length: 24 }, (_, i) => i);

  // Golden stars orbiting the mascot (radii for 72px orb)
  stars = [
    { size: 9,  style: this.getStarStyle(0, 46, 6, 1.8, 0) },
    { size: 7,  style: this.getStarStyle(72, 42, 8, 2.2, 0.5) },
    { size: 10, style: this.getStarStyle(144, 50, 7, 1.5, 1.2) },
    { size: 6,  style: this.getStarStyle(216, 40, 9, 2.5, 0.8) },
    { size: 8,  style: this.getStarStyle(288, 48, 5, 1.9, 1.6) },
  ];

  // Precomputed route line endpoints (12 lines radiating from center at 100,100)
  routeLines = Array.from({ length: 12 }, (_, i) => {
    const angle = i * 30;
    const rad = angle * Math.PI / 180;
    return {
      x2: Math.round(100 + 95 * Math.cos(rad)),
      y2: Math.round(100 + 95 * Math.sin(rad)),
      dotX: Math.round(100 + 90 * Math.cos(rad)),
      dotY: Math.round(100 + 90 * Math.sin(rad)),
      lineBegin: (0.85 + i * 0.03).toFixed(2) + 's',
      dotBegin: (1.1 + i * 0.03).toFixed(2) + 's'
    };
  });

  private bounceInterval: any;
  private destroy$ = new Subject<void>();

  constructor(private globalService: GlobalService) {}

  ngOnInit(): void {
    this.startBouncing();
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);
  }

  ngOnDestroy(): void {
    if (this.bounceInterval) {
      clearInterval(this.bounceInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startBouncing(): void {
    this.bounceInterval = setInterval(() => {
      this.bounceState = this.bounceState === 'idle' ? 'up' : 'idle';
    }, 1500);
  }

  onMascotClick(): void {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    if (this.bounceInterval) {
      clearInterval(this.bounceInterval);
      this.bounceInterval = null;
    }

    // After transition completes, open the game
    setTimeout(() => {
      this.mascotClicked.emit();
      setTimeout(() => {
        this.isTransitioning = false;
        this.startBouncing();
      }, 400);
    }, 2500);
  }

  getParticleStyle(i: number): { [key: string]: string } {
    const angle = (i / 24) * 360;
    const rad = angle * Math.PI / 180;
    const delay = (i * 65) + 300;
    const radius = 30 + (((i * 7 + 13) % 17) / 17) * 40; // deterministic pseudo-random
    const endX = Math.round(Math.cos(rad) * radius);
    const endY = Math.round(Math.sin(rad) * radius);
    const midX = Math.round(Math.cos(rad) * radius * 0.3);
    const midY = Math.round(Math.sin(rad) * radius * 0.3);
    return {
      '--end-x': endX + 'vmin',
      '--end-y': endY + 'vmin',
      '--mid-x': midX + 'vmin',
      '--mid-y': midY + 'vmin',
      'animation-delay': delay + 'ms'
    };
  }

  private getStarStyle(startAngleDeg: number, orbitRadiusPx: number, orbitDurationSec: number, twinkleDurationSec: number, delayOffset: number): { [key: string]: string } {
    return {
      '--start-angle': startAngleDeg + 'deg',
      '--orbit-radius': orbitRadiusPx + 'px',
      '--orbit-duration': orbitDurationSec + 's',
      '--orbit-delay': delayOffset + 's',
      '--twinkle-duration': twinkleDurationSec + 's',
      '--twinkle-delay': (delayOffset * 0.7) + 's',
    };
  }
}
